import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useSubscription } from '../SubscriptionContext';
import { FaLock, FaStar, FaRobot, FaChartBar, FaBriefcase } from 'react-icons/fa';
import './SubscriptionGuard.css';

const SubscriptionGuard = ({ children, requirePaid = false }) => {
  const { user, loading: authLoading } = useAuth();
  const { loading: subLoading, isPaidUser, isFreeUser, canGenerateQuestions, getRemainingQuestions, getUsageInfo } = useSubscription();
  const navigate = useNavigate();
  const [initialLoad, setInitialLoad] = React.useState(true);

  // Function to navigate to pricing section
  const navigateToPricing = () => {
    navigate('/');
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Mark initial load as complete after first check
  React.useEffect(() => {
    if (!authLoading && !subLoading) {
      setInitialLoad(false);
    }
  }, [authLoading, subLoading]);

  // ONLY show loading screen on very first load when user is unknown
  // Don't show it when returning to page - this preserves form state
  if (authLoading && initialLoad && !user) {
    return (
      <div className="subscription-guard-container">
        <div className="subscription-guard-card">
          <div className="loading-spinner"></div>
          <h2>Checking access...</h2>
          <p>Please wait while we verify your subscription.</p>
        </div>
      </div>
    );
  }

  // If user is not authenticated after loading, redirect to home
  if (!authLoading && !user) {
    return <Navigate to="/" replace />;
  }

  // If paid subscription is required but user doesn't have one
  if (requirePaid && !isPaidUser()) {
    return (
      <div className="subscription-guard-container">
        <div className="subscription-guard-card">
          <div className="icon-wrapper">
            <FaLock className="react-icon" />
          </div>
          <h2>Premium Access Required</h2>
          <p>This feature is only available to paid subscribers.</p>
          <p>Upgrade to our Standard Plan to access the full platform.</p>
          
          <div className="guard-actions">
            <button 
              onClick={navigateToPricing}
              className="upgrade-button"
            >
              Upgrade Now
            </button>
            <button 
              onClick={() => navigate('/')}
              className="home-button"
            >
              Back to Home
            </button>
          </div>

          <div className="features-preview">
            <h3>Premium Features:</h3>
            <ul>
              <li><FaStar className="feature-icon" /> 50 questions per month</li>
              <li><FaRobot className="feature-icon" /> Personalized AI feedback</li>
              <li><FaChartBar className="feature-icon" /> Real-time answer analysis</li>
              <li><FaBriefcase className="feature-icon" /> Advanced interview preparation</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // For free users or when paid subscription is not required, check usage limits
  if (!requirePaid && isFreeUser() && !canGenerateQuestions()) {
    const usageInfo = getUsageInfo();
    
    return (
      <div className="subscription-guard-container">
        <div className="subscription-guard-card">
          <div className="icon-wrapper">
            <FaRobot className="react-icon" />
          </div>
          <h2>Free Trial Limit Reached</h2>
          <p>You've used your free questions ({usageInfo?.used}/{usageInfo?.limit}).</p>
          <p>Upgrade to the Standard Plan to get 50 questions per month and unlock all features!</p>
          
          <div className="guard-actions">
            <button 
              onClick={navigateToPricing}
              className="upgrade-button"
            >
              Upgrade to Standard Plan
            </button>
            <button 
              onClick={() => navigate('/')}
              className="home-button"
            >
              Back to Home
            </button>
          </div>

          <div className="features-preview">
            <h3>What you'll get with Standard Plan:</h3>
            <ul>
              <li><FaStar className="feature-icon" /> 50 questions per month</li>
              <li><FaRobot className="feature-icon" /> Personalized AI feedback</li>
              <li><FaChartBar className="feature-icon" /> Real-time answer analysis</li>
              <li><FaBriefcase className="feature-icon" /> Advanced interview preparation</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // If user has access, render the protected content
  return children;
};

export default SubscriptionGuard;